import { Navigate, useLocation, Outlet } from 'react-router-dom';
import ErrorBoundary from 'app/ErrorBoundary';
import { ROUTES } from 'types/navigation';
import { Loader } from 'components/UI/Loader';
import {
	useAccount,
	useSubscription,
	useWithProject,
	usePermissionAccess,
	useSubscriptionRules
} from 'hooks/store';
import { useEffect, useState } from 'react';
import { getUserId } from 'features/communication-bridge/userIdStore';
import {
	useCommunicationBridge,
	type MessageFromShell
} from 'features/communication-bridge/CommunicationBridgeProvider';
import { identify } from 'app/tracking/TrackingProvider';
import { LastPaymentWarning } from 'components/Account/LastPaymentWarning/LastPaymentWarning';
import { LicenseLimitationModal } from '../Account/Subscription/Modals/LicenseLimitationModal';
import { AccountUMProvider } from 'components/Providers';
import { useErrorReportingIdentification } from 'features/error-reporting/useErrorReportingIdentification';

export function WithSubscriptionRules() {
	const [{ fetched: isAccountFetched }] = useAccount();
	const [
		{
			data: {
				subscriptionTypes: { isLedidiEnterprise },
				userTypesAndRoles: { isSubscriptionOwner }
			},
			fetched: isSubscriptionFetched
		}
	] = useSubscription();

	const { showEnterpriseAdminRoute } = useSubscriptionRules();

	if (!isAccountFetched) return <Loader primary />;

	if (isSubscriptionFetched && (!isSubscriptionOwner || isLedidiEnterprise)) {
		if (showEnterpriseAdminRoute) {
			<Navigate to={ROUTES.EnterpriseAdminSubscription} replace />;
		} else return <Navigate to={ROUTES.AccountUMSubscription} replace />;
	}

	// This never happens ???
	return <Outlet />;
}

export function WithLoggedInComponent() {
	useWithProject();
	usePermissionAccess();

	useSegmentIdentification();
	useErrorReportingIdentification();

	const { pathname } = useLocation();

	return (
		<ErrorBoundary pathname={pathname}>
			<LastPaymentWarning />
			<LicenseLimitationModal />
			<AccountUMProvider />

			<Outlet />
		</ErrorBoundary>
	);
}

const useSegmentIdentification = () => {
	const [
		{
			data: { details: userDetails }
		}
	] = useSubscription();

	// The studies-service user id is injected by the shell via postMessage.
	// We identify with it (instead of the Cognito sub) so Mixpanel's Original
	// ID Merge API lands shell and legacy events on the same distinct_id.
	const { addEventListener, removeEventListener } = useCommunicationBridge();
	const [userId, setUserId] = useState<string | null>(getUserId());
	useEffect(() => {
		const listener = (message: MessageFromShell) => {
			if (message.type === 'setUserId') setUserId(message.userId);
		};
		addEventListener(listener);
		return () => removeEventListener(listener);
	}, [addEventListener, removeEventListener]);

	useEffect(() => {
		if (!userId) return;
		try {
			const subscriptionId = userDetails?.subscriptionId || '';
			identify({
				userId,
				trait: { subscriptionId: subscriptionId }
			});
		} catch (error) {
			console.error('Failed to identify user for Segment', error);
		}
	}, [userDetails, userId]);
};
